import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AnswerBank } from '../../../../core/interfaces/answer-bank';
import { MathTextComponent } from '../../../../shared/math/math-text.component';

@Component({
  selector: 'app-quiz-question',
  standalone: true,
  imports: [MathTextComponent, CommonModule, TranslateModule],
  templateUrl: './quiz-question.component.html',
  styleUrl: './quiz-question.component.css',
})
export class QuizQuestionComponent {
  @Input() questionLabel: string = '';
  @Input() questionNumber: number = 1;
  @Input() totalQuestions: number = 1;
  @Input() title: string = '';
  @Input() content: string = '';
  @Input() answers: AnswerBank[] = [];
  @Input() selectedAnswers: number[] = [];
  @Input() questionType: string = 'single_choice'; // 'single_choice', 'multiple_choice', 'true_false'
  @Input() points: number = 0;
  @Input() showPoints: boolean = false;
  @Input() disabled: boolean = false; // New: Disable interaction for timed out questions
  @Output() answerSelected = new EventEmitter<number[]>();

  get isRTL(): boolean {
    return localStorage.getItem('lang') === 'ar';
  }

  get isMultipleChoice(): boolean {
    return this.questionType?.toLowerCase().includes('multiple');
  }

  get isTrueFalse(): boolean {
    return this.questionType?.toLowerCase().includes('true_false');
  }

  selectAnswer(answerId: number, event?: Event): void {
    // Prevent selection if disabled
    if (this.disabled) {
      event?.preventDefault();
      event?.stopPropagation();
      return;
    }

    // Prevent the event from bubbling if it's a checkbox/radio change
    if (event) {
      event.stopPropagation();
    }

    if (this.isMultipleChoice) {
      this.toggleAnswer(answerId);
    } else {
      // Single choice - replace all selections
      this.answerSelected.emit([answerId]);
    }
  }

  toggleAnswer(answerId: number): void {
    if (this.disabled) return;

    const current = [...this.selectedAnswers];
    const index = current.indexOf(answerId);

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(answerId);
    }

    this.answerSelected.emit(current);
  }

  isSelected(answerId: number): boolean {
    return this.selectedAnswers.includes(answerId);
  }

  getAnswerLabel(index: number): string {
    // Convert index to letter (A, B, C, D, etc.)
    return String.fromCharCode(65 + index);
  }

  getQuestionTypeBadgeClass(): string {
    if (this.isMultipleChoice) return 'bg-info';
    if (this.isTrueFalse) return 'bg-warning';
    return 'bg-secondary';
  }

  getQuestionTypeBadgeText(): string {
    if (this.isMultipleChoice) return 'QUIZ.MULTIPLE_CHOICE';
    if (this.isTrueFalse) return 'QUIZ.TRUE_FALSE';
    return 'QUIZ.SINGLE_CHOICE';
  }
}
